import type { ReactNode } from 'react'
import type { Id } from '@/infra/id'
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardTitle,
  CardTitleCount,
  CardTabs,
} from '@/components/Card'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownScroll,
  DropdownTitle,
} from '@/components/Dropdown'
import { FileButton } from '@/components/FileButton'
import { IconButton } from '@/components/IconButton'
import { SectionHeaderAction } from '@/components/Section'
import { useToggle } from '@/hooks/state-hooks'
import styles from './DocumentsCard.module.css'
import { TabBar, TabItem } from '@/components/TabBar'
import { SearchField } from '@/components/SearchField'
import { type DocumentsSectionTab } from '@/features/jobs/details/JobDocumentsViewModel'

export interface DocumentsCardProps {
  showDownloadAll: boolean
  isDownloadAllPending: boolean
  onDownloadAll: () => unknown
  actions?: ReactNode
  showTabs: boolean
  activeTab: DocumentsSectionTab
  onTabChange: (tab: DocumentsSectionTab) => unknown
  children?: ReactNode
}

export function DocumentsCard({
  showDownloadAll,
  isDownloadAllPending,
  onDownloadAll,
  actions,
  showTabs,
  activeTab,
  onTabChange,
  children,
}: DocumentsCardProps) {
  return (
    <Card unconstrained>
      <CardHeader compact>
        <CardTitle cardLayout testId="documents-tab-item-new">
          Documentation
        </CardTitle>
        <CardHeaderActions>
          {showDownloadAll && (
            <SectionHeaderAction
              tooltip="Download all documents"
              isDisabled={isDownloadAllPending}
              onPress={onDownloadAll}
            >
              <Icon name="download" />
            </SectionHeaderAction>
          )}
          {actions}
        </CardHeaderActions>
      </CardHeader>
      {showTabs && (
        <CardTabs bordered>
          <TabBar>
            <TabItem
              cardLayout
              testId="documents-tab-item-checklist"
              active={activeTab === 'ClientChecklist'}
              onPress={() => onTabChange('ClientChecklist')}
            >
              Client checklist
            </TabItem>
            <TabItem
              cardLayout
              testId="documents-tab-item-documents"
              active={activeTab === 'Documents'}
              onPress={() => onTabChange('Documents')}
            >
              Documents
            </TabItem>
          </TabBar>
        </CardTabs>
      )}
      <CardContent scroll listContent>
        {children}
      </CardContent>
    </Card>
  )
}

export interface DocumentActionsDropdownType {
  id: Id
  name: string
  onUpload: (files: FileList) => unknown
}

export interface DocumentActionsDropdownProps {
  /**
   * Renders a labelled Upload button instead of the icon trigger.
   */
  labelled?: boolean
  hasDocuments: boolean
  canUploadDocs: boolean
  /**
   * Named document types, already narrowed by {@link typeQuery}.
   */
  documentTypes: DocumentActionsDropdownType[]
  onUploadOther?: (files: FileList) => unknown
  typeQuery: string
  onTypeQueryChange: (query: string) => unknown
  onLinkExisting: () => unknown
  canDownloadDocs: boolean
  canRemoveDocs: boolean
  hasDocumentsSelected: boolean
  selectedCount: number
  onDownloadSelected: () => unknown
  onDeleteSelected: () => unknown
}

export function DocumentActionsDropdown({
  labelled,
  hasDocuments,
  canUploadDocs,
  documentTypes,
  onUploadOther,
  typeQuery,
  onTypeQueryChange,
  onLinkExisting,
  canDownloadDocs,
  canRemoveDocs,
  hasDocumentsSelected,
  selectedCount,
  onDownloadSelected,
  onDeleteSelected,
}: DocumentActionsDropdownProps) {
  const dropdownOpened = useToggle(false)
  const toggle = dropdownOpened.toggle
  const handleClose = () => {
    dropdownOpened.disable()
    onTypeQueryChange('')
  }

  return (
    <Dropdown
      width="medium"
      minWidth={400}
      isOpen={dropdownOpened.isEnabled}
      onRequestClose={handleClose}
      position="bottom-end"
      button={
        labelled ? (
          <RawButton className={styles.uploadButton} onPress={toggle}>
            <Icon name="upload" size={14} aria-hidden="true" />
            Upload
          </RawButton>
        ) : (
          <div className={styles.moreButton}>
            <IconButton
              name={hasDocuments ? 'more' : 'add'}
              label="Add document"
              onPress={toggle}
            />
          </div>
        )
      }
      testId="add-document-dropdown-new"
    >
      <DropdownScroll>
        <DropdownTitle>Add new</DropdownTitle>
        {canUploadDocs && (
          <>
            <div className={styles.typeSearchContainer}>
              <SearchField
                size="small"
                appearance="plain"
                stretch
                placeholder="Search document types..."
                value={typeQuery}
                onChange={onTypeQueryChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            {documentTypes.map((type) => (
              <DropdownItem key={type.id} relative>
                <FileButton
                  overlay
                  onChange={(files: FileList) => {
                    type.onUpload(files)
                    toggle()
                    onTypeQueryChange('')
                  }}
                  data-test={`upload-document-input-${type.name}`}
                />
                {type.name}
              </DropdownItem>
            ))}
            {!typeQuery.trim() && onUploadOther && (
              <DropdownItem relative>
                <FileButton
                  overlay
                  multiple={true}
                  onChange={(files: FileList) => {
                    onUploadOther(files)
                    toggle()
                    onTypeQueryChange('')
                  }}
                  data-test="upload-document-input-new"
                />
                Other documents
              </DropdownItem>
            )}
            {typeQuery.trim() && documentTypes.length === 0 && (
              <div className={styles.noResults}>No matching types</div>
            )}
          </>
        )}
        {canUploadDocs && (
          <>
            <DropdownDivider />
            <DropdownTitle>Actions</DropdownTitle>
            <DropdownItem
              onPress={onLinkExisting}
              testId="add-file-link-existing-dropdown-item-new"
            >
              Link existing
            </DropdownItem>
          </>
        )}

        {(canDownloadDocs || canRemoveDocs) && hasDocumentsSelected && (
          <>
            {canDownloadDocs && (
              <DropdownItem relative onPress={onDownloadSelected}>
                Download selected
                <CardTitleCount>({selectedCount})</CardTitleCount>
              </DropdownItem>
            )}
            {canRemoveDocs && (
              <DropdownItem relative onPress={onDeleteSelected}>
                Delete selected
                <CardTitleCount>({selectedCount})</CardTitleCount>
              </DropdownItem>
            )}
          </>
        )}
      </DropdownScroll>
    </Dropdown>
  )
}
